/**
 * Document Service
 * Service for managing documents with Azure Blob Storage integration
 * Based on the Document API specification
 */

import { BaseService } from './BaseService';
import { createErrorFromResponse } from './backendApiService';
import { convertUTCDatesToLocal } from '../utils/dateUtils';
import OfficePreviewService from './officePreviewService';

/**
 * Helper to convert dates in document objects from UTC to local
 * @param {Object|Array} data - Document or array of documents
 * @returns {Object|Array} Data with converted dates
 */
const convertDocumentDates = (data) => {
    return convertUTCDatesToLocal(data, ['createdAt', 'lastModifiedAt', 'uploadedAt', 'modifiedAt']);
};

/**
 * Document Service Class
 * Provides methods for document upload, download, management, and search
 */
export class DocumentService extends BaseService {
    constructor() {
        super('/api/v1/documents', ['createdAt', 'lastModifiedAt', 'uploadedAt', 'modifiedAt']);
    }

    /**
     * Upload a document to Azure Blob Storage
     * @param {string} accessToken - JWT access token
     * @param {File} file - The file to upload
     * @param {string} container - Container name
     * @param {string} [fileName] - Optional custom filename
     * @returns {Promise<Object>} Upload response with document details
     */
    async upload(accessToken, file, container, fileName = null) {
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('container', container);
            if (fileName) {
                formData.append('fileName', fileName);
            }

            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
            const response = await fetch(`${API_BASE_URL}${this.baseUrl}/upload`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: formData,
                mode: 'cors',
                credentials: 'omit',
            });

            if (!response.ok) {
                throw await createErrorFromResponse(response, `${API_BASE_URL}${this.baseUrl}/upload`, 'POST');
            }

            const result = await response.json();
            return convertDocumentDates(result);
        } catch (error) {
            console.error('Failed to upload document:', error);
            throw error;
        }
    }

    /**
     * Get document metadata by ID
     * @param {string} accessToken - JWT access token
     * @param {number} documentId - Document ID
     * @returns {Promise<Object>} Document metadata
     */
    async getById(accessToken, documentId) {
        try {
            const result = await this.makeRequest(accessToken, `/${documentId}`);
            return convertDocumentDates(result);
        } catch (error) {
            console.error(`Failed to fetch document ${documentId}:`, error);
            throw error;
        }
    }

    /**
     * Get document metadata by container and filename
     * @param {string} accessToken - JWT access token
     * @param {string} container - Container name
     * @param {string} fileName - File name
     * @returns {Promise<Object>} Document metadata
     */
    async getByPath(accessToken, container, fileName) {
        try {
            const result = await this.makeRequest(
                accessToken,
                `/container/${encodeURIComponent(container)}/file/${encodeURIComponent(fileName)}`
            );
            return convertDocumentDates(result);
        } catch (error) {
            console.error(`Failed to fetch document ${container}/${fileName}:`, error);
            throw error;
        }
    }

    /**
     * Download a document
     * @param {string} accessToken - JWT access token
     * @param {number} documentId - Document ID
     * @returns {Promise<Blob>} File blob
     */
    async download(accessToken, documentId) {
        try {
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
            const response = await fetch(`${API_BASE_URL}${this.baseUrl}/${documentId}/download`, {
                method: 'GET',
                mode: 'cors',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Accept': 'application/octet-stream, application/pdf, image/*, */*'
                },
            });

            if (!response.ok) {
                throw await createErrorFromResponse(response, `${API_BASE_URL}${this.baseUrl}/${documentId}/download`, 'GET');
            }

            // Get the blob
            const blob = await response.blob();
            
            // Verify blob has content
            if (blob.size === 0) {
                console.error('Downloaded blob is empty');
                throw new Error('Downloaded file is empty');
            }

            return blob;
        } catch (error) {
            console.error(`Failed to download document ${documentId}:`, error);
            throw error;
        }
    }

    /**
     * List all documents in a container
     * @param {string} accessToken - JWT access token
     * @param {string} container - Container name
     * @param {string} [prefix] - Optional filename prefix filter
     * @returns {Promise<Object>} List response with documents array
     */
    async listByContainer(accessToken, container, prefix = null) {
        try {
            const params = new URLSearchParams();
            if (prefix) {
                params.append('prefix', prefix);
            }

            const url = `/container/${encodeURIComponent(container)}${params.toString() ? '?' + params.toString() : ''}`;
            const result = await this.makeRequest(accessToken, url);
            return convertDocumentDates(result);
        } catch (error) {
            console.error(`Failed to list documents in container ${container}:`, error);
            throw error;
        }
    }

    /**
     * Search documents by filename pattern
     * @param {string} accessToken - JWT access token
     * @param {string} pattern - Search pattern
     * @param {string} [container] - Optional container filter
     * @returns {Promise<Object>} Search response with documents array
     */
    async search(accessToken, pattern, container = null) {
        try {
            const params = new URLSearchParams({ pattern });
            if (container) {
                params.append('container', container);
            }

            const result = await this.makeRequest(accessToken, `/search?${params.toString()}`);
            return convertDocumentDates(result);
        } catch (error) {
            console.error(`Failed to search documents with pattern ${pattern}:`, error);
            throw error;
        }
    }

    /**
     * Count documents in a container
     * @param {string} accessToken - JWT access token
     * @param {string} container - Container name
     * @param {string} [prefix] - Optional filename prefix filter
     * @returns {Promise<Object>} Count response
     */
    async count(accessToken, container, prefix = null) {
        try {
            const params = new URLSearchParams();
            if (prefix) {
                params.append('prefix', prefix);
            }

            const url = `/container/${encodeURIComponent(container)}/count${params.toString() ? '?' + params.toString() : ''}`;
            return await this.makeRequest(accessToken, url);
        } catch (error) {
            console.error(`Failed to count documents in container ${container}:`, error);
            throw error;
        }
    }

    /**
     * Get recent documents (last 24 hours)
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Object>} Recent documents response
     */
    async getRecent(accessToken) {
        try {
            const result = await this.makeRequest(accessToken, '/recent');
            return convertDocumentDates(result);
        } catch (error) {
            console.error('Failed to fetch recent documents:', error);
            throw error;
        }
    }

    /**
     * Get container statistics
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Object>} Container statistics
     */
    async getContainerStats(accessToken) {
        try {
            return await this.makeRequest(accessToken, '/statistics/containers');
        } catch (error) {
            console.error('Failed to fetch container statistics:', error);
            throw error;
        }
    }

    /**
     * Get all container names
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Object>} Response with containers array
     */
    async getAllContainers(accessToken) {
        try {
            return await this.makeRequest(accessToken, '/containers');
        } catch (error) {
            console.error('Failed to fetch containers:', error);
            throw error;
        }
    }

    /**
     * Update document's last modified timestamp
     * @param {string} accessToken - JWT access token
     * @param {number} documentId - Document ID
     * @returns {Promise<Object>} Updated document
     */
    async touch(accessToken, documentId) {
        try {
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
            const response = await fetch(`${API_BASE_URL}${this.baseUrl}/${documentId}/touch`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw await createErrorFromResponse(response, `${API_BASE_URL}${this.baseUrl}/${documentId}/touch`, 'PUT');
            }

            const result = await response.json();
            return convertDocumentDates(result);
        } catch (error) {
            console.error(`Failed to touch document ${documentId}:`, error);
            throw error;
        }
    }

    /**
     * Rename a document by ID
     * @param {string} accessToken - JWT access token
     * @param {number} documentId - Document ID
     * @param {string} newFileName - New filename
     * @returns {Promise<Object>} Updated document
     */
    async rename(accessToken, documentId, newFileName) {
        try {
            const params = new URLSearchParams({ newFileName: newFileName.trim() });
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
            
            const response = await fetch(`${API_BASE_URL}${this.baseUrl}/${documentId}/rename?${params.toString()}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw await createErrorFromResponse(response, `${API_BASE_URL}${this.baseUrl}/${documentId}/rename`, 'PUT');
            }

            const result = await response.json();
            return convertDocumentDates(result);
        } catch (error) {
            console.error(`Failed to rename document ${documentId}:`, error);
            throw error;
        }
    }

    /**
     * Rename a document by container and filename
     * @param {string} accessToken - JWT access token
     * @param {string} container - Container name
     * @param {string} oldFileName - Current filename
     * @param {string} newFileName - New filename
     * @returns {Promise<Object>} Updated document
     */
    async renameByPath(accessToken, container, oldFileName, newFileName) {
        try {
            const params = new URLSearchParams({ newFileName: newFileName.trim() });
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
            
            const response = await fetch(
                `${API_BASE_URL}${this.baseUrl}/container/${encodeURIComponent(container)}/file/${encodeURIComponent(oldFileName)}/rename?${params.toString()}`,
                {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!response.ok) {
                throw await createErrorFromResponse(response, `${API_BASE_URL}${this.baseUrl}/rename`, 'PUT');
            }

            return await response.json();
        } catch (error) {
            console.error(`Failed to rename document ${container}/${oldFileName}:`, error);
            throw error;
        }
    }

    /**
     * Delete a document by ID
     * @param {string} accessToken - JWT access token
     * @param {number} documentId - Document ID
     * @returns {Promise<Object>} Deletion confirmation
     */
    async deleteById(accessToken, documentId) {
        try {
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
            const response = await fetch(`${API_BASE_URL}${this.baseUrl}/${documentId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Delete failed: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            console.error(`Failed to delete document ${documentId}:`, error);
            throw error;
        }
    }

    /**
     * Delete a document by container and filename
     * @param {string} accessToken - JWT access token
     * @param {string} container - Container name
     * @param {string} fileName - File name
     * @returns {Promise<Object>} Deletion confirmation
     */
    async deleteByPath(accessToken, container, fileName) {
        try {
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
            const response = await fetch(
                `${API_BASE_URL}${this.baseUrl}/container/${encodeURIComponent(container)}/file/${encodeURIComponent(fileName)}`,
                {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                    },
                }
            );

            if (!response.ok) {
                throw await createErrorFromResponse(response, `${API_BASE_URL}${this.baseUrl}/container/${container}/file/${fileName}`, 'DELETE');
            }

            return await response.json();
        } catch (error) {
            console.error(`Failed to delete document ${container}/${fileName}:`, error);
            throw error;
        }
    }

    /**
     * Check synchronization between database and blob storage
     * @param {string} accessToken - JWT access token
     * @param {string} container - Container name
     * @returns {Promise<Object>} Sync check results
     */
    async syncCheck(accessToken, container) {
        try {
            return await this.makeRequest(
                accessToken,
                `/container/${encodeURIComponent(container)}/sync-check`
            );
        } catch (error) {
            console.error(`Failed to perform sync check for container ${container}:`, error);
            throw error;
        }
    }

    /**
     * Trigger browser download for a blob
     * @param {Blob} blob - File blob
     * @param {string} fileName - Filename for download
     */
    downloadFile(blob, fileName) {
        try {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            
            // Clean up after a delay to ensure download starts
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            }, 100);
        } catch (error) {
            console.error('Failed to trigger download:', error);
            throw error;
        }
    }

    /**
     * Open blob in a new browser tab (for viewing)
     * Enhanced to support Office 365 online preview for Office documents
     * @param {Blob} blob - File blob
     * @param {string} fileName - Filename for reference
     * @param {string} [msalAccessToken] - Optional MSAL access token for Office preview
     * @param {string} [publicUrl] - Optional public URL for Office preview
     */
    async openFile(blob, fileName, msalAccessToken = null, publicUrl = null) {
        try {
            // Check if it's an Office document and we have MSAL token
            if (msalAccessToken && OfficePreviewService.isOfficeDocument(fileName)) {
                try {
                    // Try Office preview first
                    await OfficePreviewService.openPreview(msalAccessToken, blob, fileName, publicUrl);
                    return; // Success - exit
                } catch (officeError) {
                    // Fall through to standard preview
                }
            }

            // Standard browser preview for non-Office files or when Office preview fails
            // Determine the correct MIME type based on file extension
            const ext = this.getFileExtension(fileName);
            let mimeType = blob.type;
            
            // If blob doesn't have a type or has generic type, set based on extension
            if (!mimeType || mimeType === 'application/octet-stream') {
                const mimeTypes = {
                    'pdf': 'application/pdf',
                    'txt': 'text/plain',
                    'jpg': 'image/jpeg',
                    'jpeg': 'image/jpeg',
                    'png': 'image/png',
                    'gif': 'image/gif',
                    'svg': 'image/svg+xml',
                    'webp': 'image/webp',
                    'mp4': 'video/mp4',
                    'webm': 'video/webm',
                    'mp3': 'audio/mpeg',
                    'wav': 'audio/wav',
                    'json': 'application/json',
                    'xml': 'application/xml',
                    'html': 'text/html',
                    'css': 'text/css'
                };
                mimeType = mimeTypes[ext] || 'application/octet-stream';
                
                // Create a new blob with the correct MIME type
                blob = new Blob([blob], { type: mimeType });
            }
            
            // Create a blob URL
            const url = window.URL.createObjectURL(blob);
            
            // Open in new window
            const newWindow = window.open(url, '_blank');

            if (!newWindow) {
                // Clean up URL if window failed to open
                window.URL.revokeObjectURL(url);
                throw new Error('Popup blocked. Please allow popups for this site.');
            }

            // Clean up the URL after the window has loaded (longer delay for larger files)
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
            }, 5000); // Increased to 5 seconds to ensure file loads
        } catch (error) {
            console.error('Failed to open file:', error);
            throw error;
        }
    }

    /**
     * Check if file supports Office 365 online preview
     * @param {string} fileName - File name
     * @returns {boolean} True if Office preview is supported
     */
    supportsOfficePreview(fileName) {
        return OfficePreviewService.isOfficeDocument(fileName);
    }

    /**
     * Get preview information for a file
     * @param {string} fileName - File name
     * @returns {Object} Preview information
     */
    getPreviewInfo(fileName) {
        return OfficePreviewService.getPreviewInfo(fileName);
    }

    /**
     * Get file extension from filename
     * @param {string} fileName - File name
     * @returns {string} File extension (lowercase)
     */
    getFileExtension(fileName) {
        if (!fileName || !fileName.includes('.')) return '';
        return fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase();
    }

    /**
     * Check if file type is viewable in browser
     * Includes Office documents that can be previewed via M365 Online
     * @param {string} fileName - File name
     * @returns {boolean} True if viewable
     */
    isViewableInBrowser(fileName) {
        const viewableExtensions = [
            // Standard browser-viewable files
            'pdf', 'txt', 'jpg', 'jpeg', 'png', 'gif', 'svg', 'webp',
            'mp4', 'webm', 'mp3', 'wav', 'json', 'xml', 'html', 'css',
            // Office documents (via M365 Online preview)
            'doc', 'docx', 'docm', 'dot', 'dotx', 'dotm',
            'xls', 'xlsx', 'xlsm', 'xlt', 'xltx', 'xltm', 'xlsb', 'csv',
            'ppt', 'pptx', 'pptm', 'pot', 'potx', 'potm', 'pps', 'ppsx', 'ppsm',
            'one'
        ];
        const ext = this.getFileExtension(fileName);
        return viewableExtensions.includes(ext);
    }
}

// Create singleton instance
const documentService = new DocumentService();

// Export both named and default for flexibility
export { documentService };
export default documentService;
